import { Injectable } from '@angular/core';
import { Action, select, Store } from '@ngrx/store';
import { combineLatest } from 'rxjs';
import { map, startWith, switchMap, tap } from 'rxjs/operators';
import { AsyncData } from '@cigna/core/interfaces';
import { DashboardPartialState } from './dashboard.reducer';
import { dashboardQuery } from './dashboard.selectors';
import {
  loadLatestUpdates,
  loadManageAccessUsers,
  loadManageAccessUserDetail,
  resetManageAccessTile,
  fetchMessages,
} from './dashboard.actions';

// TODO: https://git.sys.cigna.com/angular-guild/cigna-nx/issues/567
// TODO: resolve CHCP disables by bot
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { User } from '@cigna/chcp/working-with-cigna/manage-access-feature';
import { MessageFilters, MessageUpdate } from '@cigna/chcp/shared/util';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { FeatureTogglesFacade } from '@cigna/features';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { DashboardWamUsersGQLService } from '@cigna/chcp/shared/graphql-util';

@Injectable({
  providedIn: 'root',
})
export class DashboardFacade {
  gqlDashboardEnabled$ = this.featureToggleFacade.featuresEnabled([
    'gqlDashboard',
  ]);

  latestUpdates$ = this.store$.pipe(select(dashboardQuery.getLatestUpdates));
  newFeatures$ = this.store$.pipe(select(dashboardQuery.getNewFeatures));
  newFeatureBanners$ = this.store$.pipe(
    select(dashboardQuery.getNewFeatureBanners),
  );
  latestUpdatesLoading$ = this.store$.pipe(
    select(dashboardQuery.getLatestUpdatesLoading),
  );

  manageAcccessDashboardData$ = this.gqlDashboardEnabled$.pipe(
    tap((gqlEnabled) => {
      if (!gqlEnabled) {
        this.resetManageAccess();
        this.getManageAccessUsers();
      }
    }),
    switchMap((enabled) =>
      enabled
        ? this.dashboardWamGql
            .watch({}, { fetchPolicy: 'cache-and-network' })
            .valueChanges.pipe(
              startWith({
                loading: true,
                data: {
                  wamDashboardUsers: [],
                },
              }),
              map((result) => ({
                loading: result.loading,
                data: {
                  wamDashboardUsers: result.data.wamDashboardUsers,
                },
              })),
            )
        : combineLatest([
            this.manageAccessUsers$,
            this.manageAccessUsersLoading$,
          ]).pipe(
            map(([users, loading]) => ({
              loading,
              data: {
                wamDashboardUsers: users,
              },
            })),
          ),
    ),
  );

  manageAccessUsers$ = this.store$.pipe(
    select(dashboardQuery.getManageAccessUsers),
  );
  manageAccessUsersLoading$ = this.store$.pipe(
    select(dashboardQuery.getManageAccessUsersLoading),
  );
  manageAccessViewingUser$ = this.store$.pipe(
    select(dashboardQuery.getManageAccessViewingUser),
  );
  manageAccessViewingUserState$ = this.store$.pipe(
    select(dashboardQuery.getManageAccessViewingUserView),
  );
  messages$ = combineLatest([
    this.store$.select(dashboardQuery.getMessagesLoading),
    this.store$.select(dashboardQuery.getMessages),
    this.store$.select(dashboardQuery.getMessagesError),
  ]).pipe(
    map(([loading, data, error]) => new AsyncData(!loading, data, error)),
  );

  constructor(
    private store$: Store<DashboardPartialState>,
    private featureToggleFacade: FeatureTogglesFacade,
    private dashboardWamGql: DashboardWamUsersGQLService,
  ) {}

  dispatch(action: Action) {
    this.store$.dispatch(action);
  }

  getLatestUpdates(): void {
    this.store$.dispatch(loadLatestUpdates());
  }

  resetManageAccess(): void {
    this.store$.dispatch(resetManageAccessTile());
  }
  getManageAccessUsers(): void {
    this.store$.dispatch(loadManageAccessUsers());
  }

  getManageAccessUserDetail(user: User): void {
    this.store$.dispatch(loadManageAccessUserDetail({ user }));
  }

  fetchMessages(filters: MessageFilters) {
    this.store$.dispatch(fetchMessages({ filters }));
  }
}
